import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/apiClient";
import ProductCard from "../components/ProductCard";
import MegaMarquee from "../components/MegaMarquee";
import Countdown from "../components/Countdown";
import PressStrip from "../components/PressStrip";
import Lookbook from "../components/Lookbook";
import Voices from "../components/Voices";
import { ArrowRight, Target, Zap, Flame } from "lucide-react";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_break-the-mold/artifacts/amlsyjnv_Screenshot%202026-06-05%20at%206.43.47%E2%80%AFPM.png";

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const featured = products.filter((p) => p.featured);
  const hero = featured[0] || products[0];
  // Arsenal shows everything; only exclude the hero when we have enough other products
  const remaining = products.length > 1 ? products.filter((p) => p.id !== hero?.id) : products;

  return (
    <div data-testid="home-page">
      {/* === HERO === */}
      <section className="relative overflow-hidden border-b border-white/20 min-h-[100vh] flex flex-col">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/30 to-transparent" />
        </div>

        {/* Floating side stamps */}
        <div className="hidden sm:flex absolute top-6 right-6 flex-col items-end gap-2 z-10">
          <span className="border border-white/30 px-3 py-1 text-[10px] tracking-[0.4em] font-mono text-bone bg-ink/40 backdrop-blur">永遠の遺産</span>
          <span className="font-mono text-blood text-[10px] tracking-[0.4em]">SYS / DROP 001 / LIVE</span>
        </div>

        <div className="hidden lg:block absolute left-6 bottom-6 z-10">
          <div className="font-mono text-bone/60 text-[10px] tracking-[0.4em] uppercase">
            <span className="text-blood">●</span> RECORDING
          </div>
          <div className="font-mono text-bone/40 text-[10px] tracking-[0.3em] mt-1">35°41'N 139°41'E</div>
        </div>

        {/* Main content */}
        <div className="relative flex-1 flex items-center mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-6 w-full">
          <div className="lg:col-span-9">
            <p className="text-blood text-xs sm:text-sm tracking-[0.4em] font-mono mb-6">
              <span className="inline-block w-3 h-3 bg-blood mr-2 align-middle animate-pulse" />
              DROP 001 — LIVE WORLDWIDE
            </p>
            <h1
              className="font-anton uppercase text-bone tracking-tighter leading-[0.82] hero-stagger"
              style={{ fontSize: "clamp(3.2rem, 11vw, 11rem)" }}
            >
              <span className="block">DISCIPLINE</span>
              <span className="block text-transparent" style={{ WebkitTextStroke: "2px #F3F3F1" }}>OVER</span>
              <span className="block text-blood glitch" data-text="EVERYTHING">EVERYTHING</span>
            </h1>

            <div className="mt-8 flex flex-wrap items-end gap-x-6 gap-y-4 max-w-2xl">
              <p className="font-marker text-bone text-2xl sm:text-3xl">we are the product of our environment.</p>
            </div>

            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to="/shop"
                data-testid="hero-shop-now-button"
                className="inline-flex items-center gap-2 px-8 py-5 bg-blood text-bone text-xs tracking-[0.4em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors group"
              >
                SHOP THE DROP
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/manifesto"
                data-testid="hero-manifesto-button"
                className="inline-flex items-center gap-2 px-8 py-5 border border-bone text-bone text-xs tracking-[0.4em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors"
              >
                READ THE CODE
              </Link>
            </div>

            <div className="mt-12 flex items-center gap-6 text-[10px] tracking-[0.3em] font-mono text-ash uppercase border-t border-white/10 pt-6 max-w-2xl">
              <span className="flex items-center gap-2"><Flame size={12} className="text-blood" /> SOLD OUT IN 4 MIN</span>
              <span className="flex items-center gap-2"><Target size={12} className="text-blood" /> 240GSM HEAVY</span>
              <span className="hidden sm:flex items-center gap-2"><Zap size={12} className="text-blood" /> LIMITED RUN</span>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-3 text-right relative">
            <div className="font-marker text-bone text-5xl rotate-[-4deg]">trust no one</div>
            <div className="font-marker text-blood text-4xl rotate-[3deg] mt-3">expect nothing</div>
            <div className="font-marker text-bone text-4xl rotate-[-2deg] mt-3">stay dangerous</div>
          </div>
        </div>

        {/* Scroll cue */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.4em] font-mono text-ash uppercase animate-bounce">
          ↓ SCROLL ↓
        </div>
      </section>

      <MegaMarquee />

      <Countdown />

      {/* === FEATURED PRODUCT SPOTLIGHT === */}
      {hero && (
        <section className="border-b border-white/20 bg-ink">
          <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 border-x border-white/15">
            <div className="relative aspect-square lg:aspect-auto bg-surface overflow-hidden group">
              <img src={hero.images?.[0]} alt={hero.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <span className="absolute top-4 left-4 bg-blood text-bone px-3 py-1 text-[10px] tracking-[0.3em] font-mono">FEATURED / 001</span>
              <span className="absolute bottom-4 left-4 font-marker text-bone text-3xl rotate-[-3deg]">heat.</span>
            </div>
            <div className="p-8 sm:p-12 lg:p-16 border-t lg:border-t-0 lg:border-l border-white/15 flex flex-col justify-center">
              <p className="text-blood text-xs tracking-[0.4em] font-mono">// SIGNATURE PIECE</p>
              <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter mt-3 leading-[0.9]">
                {hero.name}
              </h2>
              {hero.tagline && (
                <p className="font-marker text-blood text-2xl mt-4">{hero.tagline.toLowerCase()}</p>
              )}
              <p className="text-ash text-sm leading-relaxed mt-6 max-w-md">{hero.description}</p>
              <div className="mt-8 flex items-baseline gap-6">
                <span className="font-anton text-bone text-4xl">${hero.price.toFixed(2)}</span>
                <span className="text-[10px] tracking-[0.3em] font-mono text-ash">// FREE SHIP $100+</span>
              </div>
              <Link
                to={`/product/${hero.slug}`}
                data-testid="spotlight-shop-link"
                className="mt-10 inline-flex w-fit items-center gap-2 px-8 py-5 bg-blood text-bone text-xs tracking-[0.4em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors group"
              >
                CLAIM IT
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <PressStrip />

      {/* === THE FULL ARSENAL === */}
      <section className="border-b border-white/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-blood text-xs tracking-[0.3em] font-mono mb-2">// THE ARSENAL</p>
              <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter leading-[0.9]">
                NEW <span className="text-blood">WEAPONS</span>
              </h2>
            </div>
            <Link
              to="/shop"
              data-testid="view-all-link"
              className="text-bone text-xs tracking-[0.3em] uppercase font-mono hover:text-blood"
            >
              SEE EVERYTHING →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {remaining.slice(0, 6).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i + 1} />
            ))}
          </div>
          {remaining.length === 0 && (
            <div data-testid="home-empty-state" className="border border-dashed border-white/20 py-20 px-6 text-center">
              <p className="text-blood text-xs tracking-[0.4em] font-mono mb-3">// VAULT LOCKED</p>
              <h3 className="font-anton uppercase text-bone text-4xl sm:text-6xl tracking-tighter">
                NEXT DROP <span className="text-blood">INCOMING</span>
              </h3>
              <p className="font-marker text-bone text-2xl mt-4">the arsenal is being restocked.</p>
              <p className="text-ash text-sm mt-2 font-mono uppercase tracking-[0.2em]">// stay close. stay dangerous.</p>
            </div>
          )}
        </div>
      </section>

      <Lookbook />

      {/* === MANIFESTO STRIP === */}
      <section className="border-b border-white/20 bg-ink relative overflow-hidden">
        <div className="absolute -right-20 top-1/2 -translate-y-1/2 font-anton text-blood/10 text-[24rem] leading-none tracking-tighter select-none pointer-events-none rotate-[-8deg]">
          永
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="text-blood text-xs tracking-[0.4em] font-mono mb-3">// THE MANIFESTO</p>
            <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter leading-[0.9]">
              WE BUILT THIS<br/>FOR THE <span className="text-blood">RUTHLESS</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-bone">
            <p className="font-marker text-blood text-3xl">control your mind or it will control you.</p>
            <p className="text-base leading-relaxed">
              TOXIC STREET IS NOT FOR EVERYONE. IT'S FOR THE KIDS WHO WAKE UP AT 5AM,
              WHO RUN THE EXTRA MILE, WHO CHOSE THE GRIND OVER COMFORT. WE'RE NOT SELLING
              FABRIC. WE'RE SELLING A MINDSET.
            </p>
            <Link
              to="/manifesto"
              data-testid="manifesto-link"
              className="inline-block px-6 py-3 border border-bone text-bone text-xs tracking-[0.3em] uppercase font-mono hover:bg-bone hover:text-ink transition-colors"
            >
              READ THE FULL CODE →
            </Link>
          </div>
        </div>
      </section>

      <Voices />

      <MegaMarquee words={["DROP 002 LOADING", "STAY DANGEROUS", "永遠の遺産"]} reverse />

      {/* === NEWSLETTER === */}
      <section className="border-b border-white/20 bg-blood relative">
        <div className="absolute inset-0 noise opacity-20" />
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-24 text-center">
          <p className="text-bone text-xs tracking-[0.4em] font-mono mb-3 opacity-70">// JOIN THE STREET</p>
          <h2 className="font-anton uppercase text-bone text-5xl sm:text-7xl tracking-tighter">
            EARLY ACCESS<br/>OR EAT DUST
          </h2>
          <p className="text-bone/80 text-sm mt-4 max-w-md mx-auto font-mono">
            DROPS SELL OUT IN MINUTES. NO ALERT, NO DRIP.
          </p>
          <form
            className="mt-8 max-w-md mx-auto flex border-2 border-bone"
            onSubmit={(e) => { e.preventDefault(); alert("WELCOME, REBEL."); }}
          >
            <input
              data-testid="newsletter-email"
              type="email"
              required
              placeholder="YOU@DANGEROUS.COM"
              className="flex-1 bg-transparent px-4 py-4 text-bone text-sm font-mono uppercase tracking-[0.15em] focus:outline-none placeholder:text-bone/50"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              className="bg-bone text-blood px-6 py-4 text-xs tracking-[0.3em] uppercase font-mono hover:bg-ink hover:text-bone transition-colors"
            >
              JOIN →
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
